import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { fmtIDR } from "../lib/format";
import { Loader2, CheckCircle2, Plus, Smartphone, Send } from "lucide-react";
import { toast } from "sonner";

export default function ProductSale({ onDone }) {
  const [catalog, setCatalog] = useState([]);
  const [ptype, setPtype] = useState("PULSA");
  const [selected, setSelected] = useState(null);
  const [customer, setCustomer] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => { api.get("/products").then((r) => setCatalog(r.data)).catch(() => {}); }, []);

  const current = catalog.find((c) => c.type === ptype);
  const items = current?.items || [];

  const confirm = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const r = await api.post("/transactions/product", {
        product_type: ptype, label: selected.label, customer_name: customer,
      });
      setSuccess(r.data);
      toast.success("Transaksi produk tersimpan");
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  if (success) {
    return (
      <div className="text-center pt-2" data-testid="product-success">
        <div className="h-20 w-20 rounded-full mx-auto flex items-center justify-center mb-4" style={{ background: "rgba(139,92,246,0.15)" }}>
          <CheckCircle2 className="h-10 w-10 text-violet-400" />
        </div>
        <h2 className="font-head text-2xl font-bold">TRANSACTION SUCCESS</h2>
        <div className="mono text-[#EAB308] mt-1">{success.txn_code}</div>
        <div className="nnx-card p-5 mt-5 text-left space-y-1">
          <Row l="Produk" r={`${success.type === "PULSA" ? "Pulsa" : "Telegram"} ${success.product?.label || ""}`} />
          <Row l="Total (IDR)" r={fmtIDR(success.deal_idr)} />
          <Row l="Profit" r={<span className="text-emerald-400">{fmtIDR(success.net_profit)}</span>} />
          <Row l="Officer" r={success.officer_name} />
          <Row l="Waktu" r={new Date(success.created_at).toLocaleTimeString("id-ID")} />
        </div>
        <button data-testid="product-new-again" onClick={() => { setSuccess(null); setSelected(null); setCustomer(""); }} className="btn-gold w-full mt-5 text-lg py-3.5 flex items-center justify-center gap-2"><Plus className="h-5 w-5" /> TRANSAKSI BARU</button>
        <button onClick={onDone} className="btn-outline w-full mt-3">Kembali ke POS</button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Smartphone className="h-5 w-5 text-violet-400" />
        <h1 className="font-head text-xl font-bold">Jual Produk</h1>
      </div>

      {/* type toggle */}
      <div className="grid grid-cols-2 gap-2">
        {[{ k: "PULSA", l: "Pulsa" }, { k: "TELE", l: "Telegram Premium" }].map((t) => (
          <button key={t.k} data-testid={`ptype-${t.k}`} onClick={() => { setPtype(t.k); setSelected(null); }}
            className={`chip py-3 font-semibold ${ptype === t.k ? "bg-violet-500 text-white border-violet-500" : ""}`}>{t.l}</button>
        ))}
      </div>

      {/* item grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {items.map((it) => (
          <button key={it.label} data-testid={`product-item-${it.label}`} onClick={() => setSelected(it)}
            className={`rounded-xl border p-3 text-left transition-all active:scale-95 ${selected?.label === it.label ? "border-[#EAB308] bg-[#EAB308]/10" : "border-[#222B3D] bg-[#111622] hover:border-[#EAB308]/50"}`}>
            <div className="font-bold text-sm">{it.label}</div>
            <div className="mono text-[#EAB308] text-sm mt-1">{fmtIDR(it.price_idr)}</div>
          </button>
        ))}
        {items.length === 0 && <div className="col-span-full text-sm text-[#64748B]">Belum ada item.</div>}
      </div>

      {selected && (
        <div className="nnx-card p-4 ring-1 ring-violet-500/40" data-testid="product-preview">
          <div className="flex items-center justify-between">
            <div>
              <div className="label">Terpilih</div>
              <div className="font-bold">{ptype === "PULSA" ? "Pulsa" : "Telegram"} {selected.label}</div>
            </div>
            <div className="text-right">
              <div className="label">Masuk Rupiah</div>
              <div className="mono font-bold text-2xl text-[#EAB308]">{fmtIDR(selected.price_idr)}</div>
            </div>
          </div>
          <input data-testid="product-customer" value={customer} onChange={(e) => setCustomer(e.target.value)} className="nnx-input mt-3 text-sm" placeholder="Customer / catatan (opsional)" />
        </div>
      )}

      <button data-testid="confirm-product" disabled={loading || !selected} onClick={confirm}
        className="btn-gold w-full text-lg py-3.5 flex items-center justify-center gap-2">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />} CONFIRM
      </button>
    </div>
  );
}

function Row({ l, r }) {
  return <div className="flex justify-between text-sm py-1"><span className="text-[#94A3B8]">{l}</span><span className="mono">{r}</span></div>;
}
